/**
 * UrgencyAndTimingCard Component
 *
 * Creates NATURAL URGENCY without being pushy:
 * - Rebates expiring soon
 * - Equipment cost inflation (5.5% annually)
 * - Increasing failure risk with age
 * - Rising utility costs
 *
 * Shows what waiting costs them, making urgency logical not emotional.
 */

import { calculateFutureEquipmentCost } from '../../../utils/calculatorFunctions';

export default function UrgencyAndTimingCard({
  rebateGroups,
  currentSystemPrice,
  currentAge,
  annualEnergyCost
}) {
  // Find rebates expiring soon
  const urgentRebates = [];
  if (rebateGroups && rebateGroups.length > 0) {
    rebateGroups[0].rebates.forEach(rebate => {
      if (rebate.daysUntilExpiration && rebate.daysUntilExpiration <= 90) {
        urgentRebates.push(rebate);
      }
    });
  }

  // Calculate equipment inflation costs
  const inflation1yr = calculateFutureEquipmentCost(currentSystemPrice, 1);
  const inflation3yr = calculateFutureEquipmentCost(currentSystemPrice, 3);
  const inflation5yr = calculateFutureEquipmentCost(currentSystemPrice, 5);

  // Calculate ongoing energy waste
  const monthlyEnergyWaste = Math.round(annualEnergyCost * 0.25 / 12); // Assuming 25% waste on old system

  // Failure risk increases
  const currentRisk = currentAge >= 15 ? 35 : currentAge >= 12 ? 20 : 10;
  const riskIn2Years = currentAge + 2 >= 15 ? 50 : currentAge + 2 >= 12 ? 35 : 20;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '25px'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #f59e0b',
        paddingBottom: '15px',
        marginBottom: '25px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#f59e0b',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          The Cost of Waiting
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '24px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          Why Timing Matters
        </h3>
        <p style={{
          margin: '8px 0 0 0',
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          Every month you delay costs you money. Here's what waiting means:
        </p>
      </div>

      {/* Urgent Rebates */}
      {urgentRebates.length > 0 && (
        <div style={{
          padding: '20px',
          background: '#fee2e2',
          borderRadius: '10px',
          border: '2px solid #ef4444',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '15px'
          }}>
            <div style={{ fontSize: '40px', flexShrink: 0 }}>⚠️</div>
            <div style={{ flex: 1 }}>
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#991b1b'
              }}>
                Rebates Expiring Soon
              </h4>

              {urgentRebates.map((rebate, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    marginBottom: idx < urgentRebates.length - 1 ? '10px' : '0',
                    border: '1px solid #fecaca'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>
                      {rebate.name}
                    </div>
                    <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '18px' }}>
                      ${rebate.amount.toLocaleString()}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#dc2626',
                    fontWeight: '600'
                  }}>
                    ⏰ Expires in {rebate.daysUntilExpiration} days
                    ({new Date(Date.now() + rebate.daysUntilExpiration * 24 * 60 * 60 * 1000).toLocaleDateString()})
                  </div>
                </div>
              ))}

              <div style={{
                marginTop: '12px',
                fontSize: '14px',
                color: '#7f1d1d',
                fontStyle: 'italic'
              }}>
                💸 Once these rebates expire, you'll pay ${urgentRebates.reduce((sum, r) => sum + r.amount, 0).toLocaleString()} more
                for the same system.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Equipment Inflation */}
      <div style={{
        padding: '20px',
        background: '#fffbeb',
        borderRadius: '10px',
        border: '2px solid #fcd34d',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '15px'
        }}>
          <div style={{ fontSize: '40px', flexShrink: 0 }}>📈</div>
          <div style={{ flex: 1 }}>
            <h4 style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: '#92400e'
            }}>
              Equipment Costs Are Rising
            </h4>
            <p style={{
              margin: '0 0 15px 0',
              fontSize: '14px',
              color: '#92400e',
              lineHeight: '1.5'
            }}>
              HVAC equipment costs increase an average of 5.5% per year due to inflation, regulatory changes,
              and supply chain factors. Here's how much more the same system will cost if you wait:
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px'
            }}>
              <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                  In 1 Year
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#d97706' }}>
                  +${inflation1yr.inflationAdded.toLocaleString()}
                </div>
              </div>

              <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                  In 3 Years
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#ea580c' }}>
                  +${inflation3yr.inflationAdded.toLocaleString()}
                </div>
              </div>

              <div style={{
                padding: '12px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                  In 5 Years
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>
                  +${inflation5yr.inflationAdded.toLocaleString()}
                </div>
              </div>
            </div>

            <div style={{
              marginTop: '12px',
              fontSize: '13px',
              color: '#92400e',
              fontWeight: '600'
            }}>
              💰 By acting now, you save ${inflation3yr.inflationAdded.toLocaleString()} vs. waiting 3 years
            </div>
          </div>
        </div>
      </div>

      {/* Ongoing Energy Waste */}
      <div style={{
        padding: '20px',
        background: '#fef3c7',
        borderRadius: '10px',
        border: '2px solid #f59e0b',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{
              margin: '0 0 6px 0',
              fontSize: '16px',
              fontWeight: '700',
              color: '#92400e'
            }}>
              💸 Wasting Energy Every Month
            </h4>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#92400e'
            }}>
              Your current system wastes approximately:
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#d97706' }}>
              ${monthlyEnergyWaste}
            </div>
            <div style={{ fontSize: '14px', color: '#92400e' }}>
              every month
            </div>
          </div>
        </div>
      </div>

      {/* Increasing Failure Risk */}
      <div style={{
        padding: '20px',
        background: '#fee2e2',
        borderRadius: '10px',
        border: '2px solid #fca5a5'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '15px'
        }}>
          <div style={{ fontSize: '40px', flexShrink: 0 }}>⚡</div>
          <div>
            <h4 style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: '#991b1b'
            }}>
              Growing Risk of Emergency Breakdown
            </h4>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              color: '#7f1d1d',
              lineHeight: '1.5'
            }}>
              Your {currentAge}-year-old system has a <strong>{currentRisk}%</strong> chance of major failure this year.
              In 2 years, that jumps to <strong>{riskIn2Years}%</strong>. An emergency replacement typically costs
              20-30% more than planned replacement, plus you'll be without comfort when you need it most.
            </p>

            <div style={{
              padding: '12px',
              background: 'white',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#7f1d1d',
              border: '1px solid #fecaca'
            }}>
              <strong>🚨 Emergency Replacement Risks:</strong> Higher prices due to urgent timeline,
              limited equipment selection, no time to compare quotes or secure financing,
              and days without heating/cooling while waiting for installation.
            </div>
          </div>
        </div>
      </div>

      {/* Summary Call to Action */}
      <div style={{
        marginTop: '25px',
        padding: '20px',
        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        borderRadius: '10px',
        border: '2px solid #93c5fd'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '15px'
        }}>
          <div style={{ fontSize: '36px', flexShrink: 0 }}>⏰</div>
          <div>
            <h4 style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: '#1e40af'
            }}>
              The Smart Choice: Plan Ahead
            </h4>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#1e40af',
              lineHeight: '1.6'
            }}>
              By planning your replacement now instead of waiting for an emergency, you'll save money on equipment
              costs, capture available rebates before they expire, stop wasting energy every month, and ensure your
              family's comfort is never interrupted. The numbers speak for themselves—waiting costs more.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
